import React from 'react';
import { useMemo, useState } from 'react';
import { TbChevronDown, TbSearch } from 'react-icons/tb';
import { Flex, Input, Menu, Text, ScrollArea, Tooltip, Checkbox } from '@mantine/core';
import { createStyles } from '@mantine/emotion';
import { TbListDetails } from 'react-icons/tb';

const useStyles = createStyles(theme => ({
  select: {
    border: `1px solid ${theme.colors.gray[1]}`,
    borderRadius: '4px',
    cursor: 'pointer'
  }
}));

export interface Fund {
  fund_id: string;
  fund_name: string;
  isSelected: boolean;
}

interface SelectFundsProps {
  funds?: Fund[];
  onChange: (fundId: string) => void;
}

export function SelectFunds({ funds = [], onChange }: SelectFundsProps) {
  const { classes } = useStyles();
  const [term, setTerm] = useState<string | undefined>();

  const filteredOptions = useMemo(() => {
    if (!funds || funds.length === 0) return [];

    if (!term) return funds;

    return funds.filter(item => item.fund_name.toLowerCase().includes(term.toLowerCase()));
  }, [funds, term]);

  const onChangeTerm = e => {
    setTerm(e.target.value);
  };

  const defaultRenderOptions = item => (
    <Menu.Item id={`select-funds-option-${item.fund_id}`} p={8} key={item.fund_id} w="100%">
      <Flex
        align="center"
        justify="space-between"
        gap="8px"
        id={`select-funds-option-row-${item.fund_id}`}
      >
        <Checkbox
          id={`select-funds-option-checkbox-${item.fund_id}`}
          size="xs"
          onChange={() => onChange(item.fund_id)}
          checked={item.isSelected}
        />
        <Tooltip label={item.fund_name} disabled={!item.fund_name}>
          <Text id={`select-funds-option-label-${item.fund_id}`} fz="14px" lh="20px" lineClamp={2}>
            {item.fund_name}
          </Text>
        </Tooltip>
      </Flex>
    </Menu.Item>
  );

  const selectedFundsSize = useMemo(() => {
    return funds?.filter(item => item.isSelected)?.length || 0;
  }, [funds]);

  return (
    <Menu id="select-funds-menu" shadow="md" width={180} closeOnItemClick={false}>
      <Menu.Target>
        <Flex
          id="select-funds-trigger"
          align="center"
          justify="space-between"
          className={classes.select}
          p="2.5px 12px"
          //@ts-ignore
          sx={{
            gap: '8px',
            border: '1px solid #CED4DA'
          }}
          maw={180}
          w={180}
          gap="8px"
        >
          <Flex align="center" gap="8px">
            <TbListDetails size={12} color="#228BE6" />
            <Text
              id="select-funds-trigger-label"
              fz="12px"
              lh="17px"
              style={{
                color: '#000'
              }}
              truncate="end"
            >
              {selectedFundsSize === (funds?.length || 0)
                ? 'All Funds'
                : `${selectedFundsSize} fund(s) selected`}
            </Text>
          </Flex>
          <TbChevronDown size={14} color="#909296" />
        </Flex>
      </Menu.Target>

      <Menu.Dropdown id="select-funds-dropdown" p="8px" w={180}>
        <Input
          id="select-funds-search-input"
          placeholder="Search"
          leftSection={<TbSearch size={16} />}
          size="xs"
          mb="8px"
          onChange={onChangeTerm}
          value={term}
        />

        <ScrollArea.Autosize
          id="select-funds-options-list"
          scrollbarSize={14}
          scrollHideDelay={500}
          mah="50vh"
        >
          <Menu.Item id="select-funds-select-all-option" p={8} key="all" w="100%" mb="8px">
            <Flex align="center" gap="8px" id="select-funds-select-all-row">
              <Checkbox
                id="select-funds-select-all-checkbox"
                size="xs"
                onChange={() => onChange('all')}
                checked={selectedFundsSize === (funds?.length || 0) && funds?.length > 0}
              />

              <Text id="select-funds-select-all-label" fz="14px" lh="20px" lineClamp={2}>
                Select All
              </Text>
            </Flex>
          </Menu.Item>
          {filteredOptions.map(item => defaultRenderOptions(item))}
        </ScrollArea.Autosize>
      </Menu.Dropdown>
    </Menu>
  );
}
